/**
 * Client-side plugin to handle coupon codes after SSR hydration
 * This ensures coupon codes from cookies/URL are applied even on initial page load
 */

import { couponMaxAge } from '@/utils/constants'

export default function (context) {
  const { app, store } = context
  // Only run on client side after hydration
  if (process.server) {
    return
  }

  // Function to apply coupon code
  const applyCouponCode = async () => {
    // Get route from window.location since route context might not be available yet
    const urlParams = new URLSearchParams(window.location.search)
    const dParam = urlParams.get('d') || ''
    const couponCodeParam = urlParams.get('couponCode') || ''
    const routeCouponCode = dParam || couponCodeParam
    const cookiesCouponCode = app.$cookies.get('couponCode')
    const couponCode = routeCouponCode || cookiesCouponCode

    if (couponCode) {
      // Set coupon code in both stores to ensure compatibility during migration
      // Set in Vuex store (still used by some components)
      await store.dispatch('setCouponCode', couponCode)

      // Also set in Pinia store (used by category/brand pages)
      if (app.$pinia) {
        try {
          // Import and use the store with the Pinia instance
          const { useRootStore } = require('@/stores/useRootStore')
          const rootStore = useRootStore(app.$pinia)
          rootStore.setCouponCode(couponCode)
        } catch (error) {
          console.error('[coupon-code-client] Error setting coupon in Pinia:', error)
        }
      }

      // Refresh current page data to show updated prices
      const currentRoute = app.router?.currentRoute || app.$route

      if (currentRoute) {
        const { name } = currentRoute

        // Refresh data based on current page type
        if (name?.includes('product')) {
          // Refresh product data to get coupon-discounted prices
          await store.dispatch('product/getProduct')
        } else if (name?.includes('category')) {
          // For category pages, extract the ID from the route name (e.g., "en-GB/category/2" -> "2")
          const categoryIdMatch = name.match(/category\/(\d+)/)
          const categoryId = categoryIdMatch ? categoryIdMatch[1] : null

          if (app.$pinia && categoryId) {
            try {
              const { useCategoryStore } = require('@/stores/useCategoryStore')
              const categoryStore = useCategoryStore(app.$pinia)
              const { useRootStore } = require('@/stores/useRootStore')
              const rootStore = useRootStore(app.$pinia)

              // Wait a bit for the initial page load to complete
              await new Promise((resolve) => setTimeout(resolve, 100))

              // Re-fetch category data with coupon
              await categoryStore.getCategory(categoryId, { rootStore })
            } catch (error) {
              console.error('[coupon-code-client] Error refreshing category:', error)
            }
          }
        } else if (name?.includes('brand')) {
          // For brand pages, extract the ID from the route name (e.g., "en-GB/brand/1" -> "1")
          const brandIdMatch = name.match(/brand\/(\d+)/)
          const brandId = brandIdMatch ? brandIdMatch[1] : null

          if (app.$pinia && brandId) {
            try {
              const { useBrandStore } = require('@/stores/useBrandStore')
              const brandStore = useBrandStore(app.$pinia)
              const { useRootStore } = require('@/stores/useRootStore')
              const rootStore = useRootStore(app.$pinia)

              // Wait a bit for the initial page load to complete
              await new Promise((resolve) => setTimeout(resolve, 100))

              // Re-fetch brand data with coupon
              await brandStore.getBrand(brandId, { rootStore })
            } catch (error) {
              console.error('[coupon-code-client] Error refreshing brand:', error)
            }
          }
        } else if (name === 'index' || name?.includes('index')) {
          // Refresh homepage data
          if (app.$pinia) {
            try {
              const { useHomepageStore } = require('@/stores/useHomepageStore')
              const homepageStore = useHomepageStore(app.$pinia)
              const { useRootStore } = require('@/stores/useRootStore')
              const rootStore = useRootStore(app.$pinia)
              await homepageStore.getHomepage({ rootStore })
            } catch (error) {
              console.error('[coupon-code-client] Error refreshing homepage:', error)
            }
          }
        }
      }
    }

    if (routeCouponCode) {
      app.$cookies.set('couponCode', routeCouponCode, { maxAge: couponMaxAge })
    }
  }

  // Apply coupon code after mount
  if (process.client) {
    // Wait for the app to be mounted
    if (app.$nuxt && app.$nuxt.$nextTick) {
      app.$nuxt.$nextTick(() => {
        applyCouponCode()
      })
    } else {
      // Fallback: use setTimeout to ensure DOM is ready
      setTimeout(() => {
        applyCouponCode()
      }, 0)
    }
  }
}
